import { apiClient } from './apiClient';
import { TAG_GROUPS, type FilterTag, type TagGroup } from '../config/filters';

/**
 * The curated colour/fabric/length/style vocabulary behind the tag picker.
 *
 * Served by `/v1/filter-tags`. A creator typing a term that isn't on the list
 * submits it for review — it stays off the public vocabulary until the team
 * approves it, which is the "Reviewed by our team" note in the picker.
 */

export type FilterTagGroupName = 'colour' | 'fabric' | 'length' | 'style';

interface ApiFilterTag {
  label: string;
  slug: string;
  group: FilterTagGroupName;
  swatch: string | null;
}

/** Group name as the API spells it → the heading the picker shows. */
const GROUP_LABELS: Record<FilterTagGroupName, string> = {
  colour: 'Colour',
  fabric: 'Fabric',
  length: 'Length',
  style: 'Style',
};

export const filterTagsService = {
  /**
   * The approved vocabulary, grouped for the picker. Falls back to the
   * bundled list in `config/filters.ts` when the request fails, so the picker
   * never renders empty.
   */
  list: async (): Promise<TagGroup[]> => {
    try {
      const response = await apiClient.get<ApiFilterTag[]>('/filter-tags');
      if (!response.success || !response.data || response.data.length === 0) {
        return TAG_GROUPS;
      }

      const byGroup = new Map<FilterTagGroupName, FilterTag[]>();
      for (const tag of response.data) {
        const list = byGroup.get(tag.group) ?? [];
        list.push({ label: tag.label, swatch: tag.swatch ?? undefined });
        byGroup.set(tag.group, list);
      }

      return (Object.keys(GROUP_LABELS) as FilterTagGroupName[])
        .map((group) => ({ name: GROUP_LABELS[group], tags: byGroup.get(group) ?? [] }))
        .filter((group) => group.tags.length > 0);
    } catch (err) {
      console.error('Failed to load filter tags, using the bundled list:', err);
      return TAG_GROUPS;
    }
  },

  /** Submit a term the creator typed. Stored as pending for review. */
  submit: async (label: string, group: FilterTagGroupName = 'style'): Promise<void> => {
    try {
      await apiClient.post('/filter-tags', { label, tag_group: group });
    } catch (err) {
      // A tag that can't be submitted still gets used on the post, so this
      // must never block the create flow.
      console.error('Failed to submit tag for review:', err);
    }
  },
};
